import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../components/navBar/navBar";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  Paper,
  Typography,
} from "@mui/material";
import useGetUserActionLogs from "../hooks/useGetUserActionLogs";
import Loader from "../components/Loader";
import { showApiErrorToast } from "../utils/helpers";
import { useState } from "react";

const columns = [
  { id: "user_email", label: "Email" },
  { id: "role", label: "User Role" },
  { id: "trial_name", label: "Trial Name" },
  { id: "patient_id", label: "Subject ID" },
  { id: "timestamp", label: "Timestamp" },
  { id: "action", label: "Activity" },
];

const SingleUserLogs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email } = location.state || {};

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const {
    data: logs = [],
    isLoading,
    isError,
    error,
  } = useGetUserActionLogs({ email, enabled: Boolean(email) });

  useEffect(() => {
    if (!email) {
      navigate("/patient/user-action-logs");
    }
  }, [email, navigate]);

  if (isError) {
    showApiErrorToast(error, "Failed to fetch user action logs");
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const paginatedLogs = logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp + "Z");
    return `${date.toDateString()}, ${date.toLocaleTimeString()}`;
  };

  const getCellValue = (log, columnId) => {
    if (columnId === "timestamp") return formatTimestamp(log.timestamp);
    if (columnId === "action") return log.action_type || log.action || "-";
    return log[columnId] || "-";
  };

  return (
    <Box>
      <NavBar active="single-user-logs" />
      <Box sx={{ height: "calc(100vh - 64px)", p: 2 }}>
        <Paper
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="h6">Activity Logs {email && `- ${email}`}</Typography>
          </Box>

          {isLoading ? (
            <Loader />
          ) : logs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexGrow: 1,
              }}
            >
              <Typography color="text.secondary">No logs found for this user.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table stickyHeader aria-label="user action logs">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          sx={{
                            fontWeight: "bold",
                            fontSize: "1.05rem",
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedLogs.map((log, index) => (
                      <TableRow key={index}>
                        {columns.map((column) => (
                          <TableCell key={column.id}>{getCellValue(log, column.id)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={logs.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default SingleUserLogs;
